import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";
import { useLang } from "../../i18n/LanguageProvider";
import { LogoMark } from "./Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_ATTEMPTS = 6;
const POLL_MS = 2000;

export const BookingSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { t } = useLang();

  const [state, setState] = useState("polling"); // polling | paid | expired | failed
  const [attempts, setAttempts] = useState(0);
  const [amount, setAmount] = useState(null);
  const [currency, setCurrency] = useState("eur");

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;

    const poll = async (n) => {
      if (n >= MAX_ATTEMPTS) {
        if (!cancelled) setState("expired");
        return;
      }
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        setAmount(data.amount_total);
        setCurrency(data.currency);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), POLL_MS);
      } catch (e) {
        if (!cancelled) setState("failed");
      }
    };

    poll(0);
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const formatAmount = () => {
    if (amount == null) return "";
    const val = (amount / 100).toFixed(2);
    return `${val} ${currency.toUpperCase()}`;
  };

  return (
    <div className="min-h-screen bg-[#0B1416] text-[#F5F1E8] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-xl w-full text-center"
      >
        <div className="flex justify-center mb-8">
          <LogoMark size={56} tone="light" />
        </div>

        {state === "polling" && (
          <>
            <Loader2
              size={48}
              className="mx-auto mb-8 animate-spin text-[#6EE3E6]"
              strokeWidth={1.2}
            />
            <h1 className="font-heading text-4xl md:text-5xl leading-tight mb-4">
              Vérification du paiement…
            </h1>
            <p className="text-sm opacity-70">
              Tentative {attempts + 1} / {MAX_ATTEMPTS}
            </p>
          </>
        )}

        {state === "paid" && (
          <>
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="flex justify-center mb-8"
            >
              <CheckCircle2 size={64} className="text-[#1BB6B9]" strokeWidth={1.2} />
            </motion.div>
            <div className="overline text-[#6EE3E6] mb-4">— Réservation confirmée</div>
            <h1 className="font-heading-tight text-5xl md:text-6xl leading-[0.95] mb-6">
              Merci.<span className="font-italic text-[#6EE3E6]"> Votre place</span> est réservée.
            </h1>
            <p className="text-base md:text-lg font-light opacity-75 max-w-md mx-auto mb-2">
              Nous avons bien reçu votre acompte remboursable de{" "}
              <span className="text-[#6EE3E6] font-medium">{formatAmount()}</span>.
            </p>
            <p className="text-sm opacity-60 max-w-md mx-auto">
              Un conseiller vous contacte sous 24h pour finaliser les détails de votre voyage.
            </p>
          </>
        )}

        {state === "expired" && (
          <>
            <XCircle size={56} className="mx-auto mb-8 text-[#C19B6C]" strokeWidth={1.2} />
            <h1 className="font-heading text-4xl md:text-5xl leading-tight mb-4">
              Session expirée
            </h1>
            <p className="text-sm opacity-70 mb-8">
              Le délai de vérification est dépassé. Vérifiez votre email ou réessayez.
            </p>
          </>
        )}

        {state === "failed" && (
          <>
            <XCircle size={56} className="mx-auto mb-8 text-[#A86A4F]" strokeWidth={1.2} />
            <h1 className="font-heading text-4xl md:text-5xl leading-tight mb-4">
              Paiement non abouti
            </h1>
            <p className="text-sm opacity-70 mb-8">
              Aucune session valide trouvée. Aucun débit n'a été effectué.
            </p>
          </>
        )}

        <div className="mt-12">
          <button
            data-testid="success-back-home"
            onClick={() => navigate("/")}
            className="tt-btn tt-btn-ghost mx-auto"
          >
            <ArrowLeft size={13} />
            Retour à l'accueil
          </button>
        </div>
      </motion.div>
    </div>
  );
};
